import { SocialSharing } from "@ionic-native/social-sharing";
import {
  IonButton,
  IonCol,
  IonIcon,
  IonInput,
  IonLoading,
  IonRow,
  isPlatform,
  useIonAlert,
  useIonToast,
  useIonViewWillEnter,
} from "@ionic/react";
import { cartOutline, logOutOutline } from "ionicons/icons";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router";
import { AppContext } from "../../providers/Redux/Reducers/AppContext";
import { isEmailValid, isPhoneNumberValid } from "../../utils/utils";
import { CustomPhoneInput } from "../CustomPhoneInput/CustomPhoneInput";
import commonFormStyles from "../../components/UI/CommonFormElements/CommonFormElements.module.scss";
import styles from "./MyAccountComponent.module.scss";
import classNames from "classnames/bind";
import { PasswordInput } from "../PasswordInput/PasswordInput";
import CommonFormElement from "../UI/CommonFormElements/CommonFormElement";
import MaxWidthContainer from "../UI/MaxWidthContainer/MaxWidthContainer";
import { ID } from "../../types";
import { CustomerAddressesSkeleton } from "./CustomerAddressesSkeleton";
import { useCustomerAddressesStateData } from "../../providers/CustomerAddresses/CustomerAddressesStateContext";
import { ValidatePasswordInput } from "../ValidatePasswordInput/ValidatePasswordInput";
import { ShippingAddressManually } from "../ShippingAddressManually/ShippingAddressManually";
import { useAuth } from "../../contexts/auth-context/AuthContextProvider";
import { Card, User, UserWithPassword } from "@core/types";
import { useDeleteMeMutation, useGetProfileQuery, useUpdateProfileMutation } from "../../providers/profile";
import { useConfig } from "../../providers/config";
import Barcode from "react-barcode";
import { useCustomerAddressesStorageData } from "../../providers/customerAddressesStorage";
import Page from "../../pages/page-layout";
import CollapsibleEntityList from "../CollapsibleEntityList/CollapsibleEntityList";
import CustomerAddress from "../CustomerAddress/CustomerAddress";
import ReferralSystemComponent from "../ReferralSystemComponent/ReferralSystemComponent";
import { useDeleteCustomerAddress, useGetCustomerAddresses } from "../../providers/CustomerAddresses";
import { CustomerAddressType } from "../../generated/graphql";
import ReferralPoints from "../ReferralPoints/ReferralPoints";
import { useGetShippingZonesQuery } from "../../providers/shippingAndPayment";
import { useOrderData } from "../../providers/orderDataStore";
import useIsViewFocused from "../../hooks/useIsViewFocused";
import { useGetCardsQuery } from "../../providers/cards";
import PaymentCard from "../PaymentCardComponent/PaymentCard";
import { useTranslation } from "react-i18next";

const cx = classNames.bind(styles);
const commonForm = classNames.bind(commonFormStyles);

type userDataType = {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  city?: string;
  street?: string;
  houseNumber?: string;
};

const comparePassword = (password: string | undefined | null, confirmPassword: string | undefined | null) => {
  return password && confirmPassword && confirmPassword !== password;
};

const MyAccountComponent = () => {
  const { t } = useTranslation();
  const config = useConfig();
  const { user, login, logout, updateUserData } = useAuth();
  const isViewFocused = useIsViewFocused();

  const { data: cardsData, isLoading: cardsLoading } = useGetCardsQuery();

  const { data: profileData } = useGetProfileQuery({
    userId: user?.id,
    options: {
      enabled: config?.hasCardLoyalty && !!user?.id,
      cacheTime: 0,
      onSuccess: (data) => {
        updateUserData({ ...user, firstName: data.firstName, lastName: data.lastName });
      },
    },
  });

  console.log(t("unexpectedErrorShippingZones"));

  const { currentShippingAddressId, setCurrentShippingAddressId } = useCustomerAddressesStorageData();

  const { data: shippingZones } = useGetShippingZonesQuery({
    onError: (err: string) => {
      presentToast({
        message: err ?? t("unexpectedErrorShippingZones"),
        duration: 2000,
        color: "danger",
      });
    },
    enabled: config?.shipping.isShippingZonesEnabled && config?.shipping.showDefaultShippingZone,
  });

  const {
    data: customerAddresses,
    isLoading: addressesLoading,
    refetch: refetchCustomerAddresses,
  } = useGetCustomerAddresses({
    customerId: user?.id || "",
    options: {
      enabled: !!user?.id,
    },
  });
  const { mutateAsync: deleteCustomerAddressAsync, isLoading: deleteCustomerAddressLoading } =
    useDeleteCustomerAddress();

  const { mutate: updateProfile } = useUpdateProfileMutation();
  const { state, dispatch } = useContext(AppContext);
  const history = useHistory();
  const [presentToast] = useIonToast();
  const { mutateAsync: deleteMeMutation } = useDeleteMeMutation();
  const { setCustomerAddress, setIsAddressModalOpen, isAddressModalOpen } = useCustomerAddressesStateData();
  const [presentAlert] = useIonAlert();

  const [showLoading, setShowLoading] = useState(false);
  const [firstName, setFirstName] = useState<string>(profileData?.firstName || user?.firstName || "");
  const [lastName, setLastName] = useState<string>(profileData?.lastName || user?.lastName || "");
  const [email, setEmail] = useState<string>(profileData?.email || user?.email || "");
  const [phone, setPhone] = useState<string>(profileData?.phone || user?.phone || "");
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [earnedPoints, setEarnedPoints] = useState(0);
  const userDataRef = useRef<userDataType>({});
  const referralLinkRef = useRef(null as any);

  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(true);
  const [passwordError, setPasswordError] = useState("");

  const isFieldsChanged = (): boolean => {
    return (
      firstName !== userDataRef.current.firstName ||
      lastName !== userDataRef.current.lastName ||
      email !== userDataRef?.current?.email ||
      phone !== userDataRef.current.phone ||
      !!password
    );
  };

  const isFormValid = (): boolean => {
    return (
      (((email ? isEmailValid(email) : true) || email === userDataRef?.current?.email) &&
        (!phone || isPhoneNumberValid(phone)) &&
        !password) ||
      (isPasswordValid && password === confirmPassword)
    );
  };

  useIonViewWillEnter(() => {
    resetComponentState(user);
  }, [user, config]);

  useEffect(() => {
    setIsSubmitButtonDisabled(!isFieldsChanged() || !isFormValid());
  }, [firstName, lastName, email, phone, password, confirmPassword]);

  const resetComponentState = (user: User | undefined) => {
    userDataRef.current = {
      firstName: user?.firstName,
      lastName: user?.lastName,
      email: user?.email,
      phone: user?.phone,
    };

    setFirstName(userDataRef.current.firstName || "");
    setLastName(userDataRef.current.lastName || "");
    setEmail(userDataRef?.current?.email || "");
    setPhone(userDataRef.current.phone || "");
    setPassword("");
    setConfirmPassword("");
    setIsPasswordValid(false);
    setIsSubmitButtonDisabled(true);
  };

  const updateInfo = () => {
    setShowLoading(true);

    const userPayload: UserWithPassword = {
      id: user?.id!,
      firstName: firstName!,
      lastName: lastName!,
      token: user?.token!,
      email: email,
      phone: phone,
      userName: user?.userName,
      password: password ?? undefined,
    };

    updateProfile(
      { userId: user?.id!, userPayload },
      {
        onError: (e) => {
          setShowLoading(false);
          if (e.error && e.error.code === "woocommerce_rest_customer_invalid_email") {
            presentToast({
              message: t("Invalid email, or such email is already in use"),
              color: "danger",
              duration: 2000,
            });
          } else {
            presentToast({
              message: t("Error Updating Data!"),
              color: "danger",
              duration: 2000,
            });
          }
        },
        onSuccess: () => {
          login(userPayload);

          setShowLoading(false);

          resetComponentState(userPayload);

          presentToast({
            message: t("Data successfully updated"),
            color: "success",
            duration: 2000,
          });

          setShowLoading(false);
        },
      },
    );
  };

  const goToMyOrders = () => {
    history.push("/my-orders");
  };

  const logoutHandler = () => {
    presentAlert({
      header: t("Confirm Logout"),
      message: t("Are you sure you want to log out?"),
      buttons: [
        {
          text: t("Cancel"),
          role: "cancel",
        },
        {
          text: t("Confirm"),
          role: "confirm",
          handler: () => {
            history.replace("/auth");
            dispatch({
              type: "REMOVE_CUSTOMER_DATA",
            });
            logout();
          },
        },
      ],
    });
  };

  const copyReferralLink = () => {
    if (isPlatform("hybrid")) {
      SocialSharing.share(`${referralLinkRef.current.value}`);
    } else {
      copyReferralLinkDesktop();
    }
  };

  const copyReferralLinkDesktop = () => {
    document.execCommand("copy");

    presentToast({
      position: "bottom",
      message: t("Copied to clipboard!"),
      color: "success",
      duration: 2000,
    });
  };

  const handleDeleteAccount = () => {
    presentAlert(t("Delete account description"), [
      {
        text: "Нет",
        role: "cancel",
      },
      {
        text: t("Yes"),
        handler: async () => {
          if (config.hasClientToDeleteUser) {
            const deleteMe = await deleteMeMutation();
            if (deleteMe?.success) {
              history.replace("/auth");
              dispatch({
                type: "REMOVE_CUSTOMER_DATA",
              });
              logout();
            }
          } else {
            window.open(config?.deleteAccountLink);
          }
        },
      },
    ]);
  };

  const [deletingAddressId, setDeletingAddressId] = useState<string | null>(null);
  // const { updateOrderData } = useOrderData();

  const deleteAddress = async (addressId: ID) => {
    setDeletingAddressId(addressId);

    try {
      await deleteCustomerAddressAsync({ addressId });
      await refetchCustomerAddresses();

      if (addressId === currentShippingAddressId) {
        const firstAvailableAddress = customerAddresses?.find((address) => address.id !== addressId);

        if (firstAvailableAddress) {
          // updateOrderData({ address: firstAvailableAddress });
          setCurrentShippingAddressId(firstAvailableAddress.id!, "MyAccountComponent");
        } else if (shippingZones) {
          setCurrentShippingAddressId("", "MyAccountComponent");
          // updateOrderData({ address: null });
        }
      }
    } catch (error) {
    } finally {
      setDeletingAddressId(null);
    }
  };

  const renderAddress = (address: Partial<CustomerAddressType>) => (
    <CustomerAddress
      key={address.id}
      address={address as CustomerAddressType}
      isDeleting={deletingAddressId === address.id}
      onPresentModal={() => {
        setCustomerAddress(address as CustomerAddressType);
        setIsAddressModalOpen(true);
      }}
      deleteAddress={() => deleteAddress(address.id!)}
    />
  );

  const renderCard = (card: Card) => <PaymentCard card={card} />;

  return (
    <div className={cx("wrapper")}>
      <IonLoading
        isOpen={showLoading || addressesLoading}
        onDidDismiss={() => setShowLoading(false)}
        message={t("Please wait...")}
        duration={20000}
      />
      <Page.HeaderDesktop />
      <MaxWidthContainer smContainer={true} classList={cx("myAccountContainer")}>
        <IonRow className={cx("myAccountButtons")}>
          <IonCol size="12" sizeMd="6">
            <IonButton
              onClick={goToMyOrders}
              color={"primary"}
              className={cx("button", "button--primary")}
              expand="block"
              type="button"
            >
              {t("Go to My orders")}
              <IonIcon icon={cartOutline} />
            </IonButton>
          </IonCol>
          <IonCol size="12" sizeMd="6">
            <IonButton
              onClick={logoutHandler}
              className={cx("button", "button--dark", "button--last")}
              expand="block"
              type="button"
            >
              {t("Log Out")}
              <IonIcon icon={logOutOutline} />
            </IonButton>
          </IonCol>
        </IonRow>
        {config?.isInviteEnabled && (
          <IonRow className={cx("mt-4 ion-no-padding referral")}>
            <IonCol size="12" className={cx("referral__title", "ion-no-padding")}>
              <h2 className={cx("myAccountFormTitle")}>
                {t("Referral system")}
                <ReferralPoints className={cx("referral__points")} />
              </h2>
            </IonCol>
            <IonCol size="12" className={cx("ion-no-padding")}>
              <ReferralSystemComponent />
            </IonCol>
          </IonRow>
        )}
        {config?.isCustomerAdressesEnabled && (
          <IonRow className="mt-4 ion-no-padding">
            <IonCol size={"12"} className={cx("customer_addresses__title", "ion-no-padding")}>
              <h2 className={cx("myAccountFormTitle")}>{t("Addresses")}</h2>
            </IonCol>
            <IonCol className="ion-no-margin ion-no-padding" size={"12"}>
              <CollapsibleEntityList
                isDeleting={deleteCustomerAddressLoading}
                entities={(customerAddresses ?? []) as CustomerAddressType[]}
                isLoading={addressesLoading}
                renderItem={renderAddress}
                renderLoading={() => <CustomerAddressesSkeleton />}
                renderEmpty={() => <p>{t("No addresses found")}</p>}
              />
            </IonCol>
          </IonRow>
        )}

        {config?.isCardsEnabled && (
          <IonRow className="mt-4 ion-no-padding">
            <IonCol size={"12"} className={cx("customer_addresses__title", "ion-no-padding")}>
              <h2 className={cx("myAccountFormTitle")}>{t("Choose Payment")}</h2>
            </IonCol>
            <IonCol className="ion-no-margin ion-no-padding" size={"12"}>
              <CollapsibleEntityList
                entities={(cardsData ?? []) as Card[]}
                isLoading={cardsLoading}
                renderItem={renderCard}
                renderLoading={() => <CustomerAddressesSkeleton />}
                renderEmpty={() => <p>{t("No cards found")}</p>}
              />
            </IonCol>
          </IonRow>
        )}

        <form
          className={cx("mt-4 myAccountForm")}
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <h2 className={cx("myAccountFormTitle")}>{t("Change account details")}</h2>

          <CommonFormElement label={t("First Name")}>
            <IonInput
              value={firstName}
              className={commonForm("commonElement")}
              type="text"
              name="firstName"
              required
              onIonChange={(e) => setFirstName(e.detail.value ?? "")}
            />
          </CommonFormElement>

          <CommonFormElement label={t("Last Name")}>
            <IonInput
              value={lastName}
              type="text"
              className={commonForm("commonElement")}
              name="lastName"
              required
              onIonChange={(e) => setLastName(e.detail.value ?? "")}
            />
          </CommonFormElement>

          {config?.updateUserForm.showPhoneNumberInput && (
            <CommonFormElement
              label={t("Contact number")}
              isValid={!phone || isPhoneNumberValid(phone)}
              errorMessage={t("Please enter a valid phone number")}
            >
              <CustomPhoneInput
                className={commonForm("commonElement", "commonElementMaskedInput", {
                  commonInvalid: phone && !isPhoneNumberValid(phone),
                })}
                value={phone}
                onCustomChange={(val, isValid) => {
                  setPhone(val);
                }}
              />
            </CommonFormElement>
          )}

          {config?.updateUserForm.isUpdatePasswordEnabled && (
            <>
              <h2 className={cx("myAccountFormTitle")}>{t("Change Password")}</h2>

              <CommonFormElement
                label={`${t("New Password")} ${t("dont fill to save current password")}`}
                isValid={!(password && !isPasswordValid)}
                errorMessage={t(passwordError)}
                classListItem={cx("myAccountFormItem")}
                classListError={cx("myAccountFormAlert")}
              >
                <ValidatePasswordInput
                  required={true}
                  value={password!}
                  setPassword={(value) => setPassword(value)}
                  setPasswordError={(error) => setPasswordError(error)}
                  setIsPasswordValid={(isValid) => setIsPasswordValid(isValid)}
                  className={commonForm("commonElement", { commonInvalid: password && !isPasswordValid })}
                />
              </CommonFormElement>
              <CommonFormElement
                label={t("Confirm New Password")}
                isValid={confirmPassword === password}
                errorMessage={!confirmPassword ? `${t("Confirm the password")}` : `${t("Password mismatch")}`}
              >
                <PasswordInput
                  required
                  value={confirmPassword}
                  className={commonForm("commonElement", {
                    commonInvalid: comparePassword(password, confirmPassword) || !!(password && !confirmPassword),
                  })}
                  onIonChange={(e) => setConfirmPassword(e.detail.value ?? "")}
                />
              </CommonFormElement>
            </>
          )}

          <IonRow className="ion-justify-content-end">
            <IonCol className="ion-no-padding" size="12" sizeMd="6">
              <IonButton
                onClick={updateInfo}
                disabled={isSubmitButtonDisabled}
                className={cx("button", "ion-no-padding")}
                expand="block"
                type="submit"
              >
                {t("Save changes")}
              </IonButton>
            </IonCol>
          </IonRow>

          {config?.hasCardLoyalty && profileData?.discountCardNumber && (
            <IonRow className={"ion-no-margin ion-no-padding ion-margin-top ion-justify-content-center"}>
              <IonCol size={"12"} className={cx("referral__title", "ion-no-padding")}>
                <h2 className={cx("myAccountFormTitle")}>{t("Discount card")}</h2>
              </IonCol>

              <IonCol className={cx("barcodeCol", "ion-no-padding")} size="12" sizeMd="6">
                <Barcode value={profileData?.discountCardNumber} format="CODE128" marginTop={16} />
              </IonCol>
            </IonRow>
          )}

          {config?.deleteAccountLink && (
            <>
              <h2 className={cx("myAccountFormTitle")}>{t("Account deletion")}</h2>

              <p className={cx("MyAccountForm_description")}>{t("Delete account description")}</p>

              <IonRow className="ion-justify-content-end">
                <IonCol className="ion-no-padding" size="12" sizeMd="6">
                  <IonButton
                    onClick={handleDeleteAccount}
                    className={cx("button", "ion-no-padding")}
                    expand="block"
                    type="button"
                    color="danger"
                  >
                    {t("Delete account")}
                  </IonButton>
                </IonCol>
              </IonRow>
            </>
          )}
        </form>

        <ShippingAddressManually
          onDismiss={() => {
            setIsAddressModalOpen(false);
          }}
          isOpen={isAddressModalOpen && isViewFocused}
        />
      </MaxWidthContainer>
      <Page.ContentFooter />
    </div>
  );
};

export { MyAccountComponent };
